import { defineStore } from "pinia";
import { ref } from "vue";
import { useDebouncedSearchQuery } from "@utils/composables/filters/useDebouncedSearchQuery";
import { Trash } from "lucide-vue-next";
import defaultJobAttributes from "@utils/constants/jobAttributes";
import { NotFoundError, ForbiddenError, UnauthorizedError } from "@routes/errors";

import useMyFetch from "@stores/useMyFetch";
import { captureEvent } from "@/lib/analytics";

export const useJobsStore = defineStore("jobs", () => {
  const jobs = ref([]);
  const jobsCount = ref(0);
  const jobSearchQuery = ref("");
  const currentPage = ref(1);
  const currentJob = ref({});
  const isNew = ref(false);
  const jobAttributes = ref({});
  const actionDropdownJobs = ref([]);
  const jobTypeFilter = ref([]);
  const mineOnlyFilter = ref(false);
  const activeFilter = ref([]);
  const jobTypeOptions = ref([]);

  // actions
  const getJobsList = async (page = 1) => {
    const { response, data } = await useMyFetch(`/jobs?page=${page}`).json().get();

    if (response.value.ok) {
      const { jobs: jobsFromQuery, jobs_count: countFromQuery, job_types: types } = data.value;
      jobs.value = jobsFromQuery;
      jobsCount.value = countFromQuery;
      if (types) jobTypeOptions.value = types;
    }
  };

  const getJob = async (id) => {
    const { response, data } = await useMyFetch(`/jobs/${id}`).json().get();
    if (response.value.status === 404) throw new NotFoundError("Page not found");
    if (response.value.status === 403) throw new ForbiddenError("User not allowed");
    if (response.value.status === 401) throw new UnauthorizedError("User not logged in");
    if (response.value.ok) {
      currentJob.value = data.value;
      jobAttributes.value = data.value.job_attributes;
    }
  };

  const createJob = async () => {
    currentJob.value.job_attributes = jobAttributes.value;
    const { response, data } = await useMyFetch(`/jobs/`).json().post(currentJob.value);

    if (response.value.ok) {
      jobs.value.unshift(data.value);
      captureEvent("job_created", { job_type: currentJob.value.job_type });
    }
  };

  const updateJob = async () => {
    currentJob.value.job_attributes = jobAttributes.value;
    const { response, data } = await useMyFetch(`/jobs/${currentJob.value.id}`).json().patch(currentJob.value);

    if (response.value.ok) {
      currentJob.value = data.value;
      captureEvent("job_updated", { job_id: currentJob.value.id });
    }
  };

  const deleteJob = async (id) => {
    const { response } = await useMyFetch(`/jobs/${id}`).json().delete();

    if (response.value.ok) {
      captureEvent("job_deleted", { job_id: id });

      const index = jobs.value.findIndex((job) => job.id === parseInt(id));
      if (index !== -1) jobs.value.splice(index, 1);
    }
  };

  const toggleActiveJob = async (job) => {
    const { response, data } = await useMyFetch(`/jobs/${job.id}`).json().patch(job);

    if (response.value.ok) updateJobList(data.value);
  };
  const runJob = async (id) => {
    const { response, data } = await useMyFetch(`/jobs/${id}/run`).json().get();

    if (response.value.ok) {
      captureEvent("job_run", { job_id: id });
      updateJobList(data.value);
    }
  };

  const cancelJob = async (id) => {
    const { response, data } = await useMyFetch(`/jobs/${id}/cancel`).json().get();

    if (response.value.ok) updateJobList(data.value);
  };

  const initRefOnCreate = () => {
    isNew.value = true;
    currentJob.value = { name: "" };
    jobAttributes.value = defaultJobAttributes();
  };

  const changePage = async (page) => {
    currentPage.value = page;
    await search(page);
  };

  const updateJobList = (data) => {
    const id = data?.id;

    const index = jobs.value.findIndex((job) => job.id === id);
    jobs.value[index] = data?.value ?? data;
  };

  const search = async (page = 1) => {
    const params = {
      name: jobSearchQuery.value,
      page: page,
      job_type: jobTypeFilter.value.join(",") || undefined,
      mine_only: mineOnlyFilter.value || undefined,
      active: activeFilter.value.length === 1 ? String(activeFilter.value[0]) : undefined,
    };
    const { response, data } = await useMyFetch(`/jobs/search`).json().post(params, null);

    if (response.value.ok) {
      const { jobs: jobsFromQuery, jobs_count: countFromQuery, job_types: types } = data.value;

      jobs.value = jobsFromQuery;
      jobsCount.value = countFromQuery;
      currentPage.value = page;
      if (types) jobTypeOptions.value = types;
    }
  };

  const toggleMineOnlyFilter = async () => {
    mineOnlyFilter.value = !mineOnlyFilter.value;
    await search();
  };

  const clearFilters = async () => {
    jobTypeFilter.value = [];
    mineOnlyFilter.value = false;
    activeFilter.value = [];
    await search();
  };

  const resetAllFilters = () => {
    jobTypeFilter.value = [];
    mineOnlyFilter.value = false;
    activeFilter.value = [];
    resetSearchQuery();
    currentPage.value = 1;
  };

  actionDropdownJobs.value = [
    {
      isDelete: true,
      function: deleteJob,
      msgConfirm: "this job",
      text: "delete",
      icon: Trash,
    },
  ];

  // jobs channel handlers
  const jobsChannelHandlers = (cable) => {
    return {
      connected: subscribeToChannel(cable),
      rejected: () => console.log("rejected"),
      received: handleReceivedMessage,
    };
  };

  const subscribeToChannel = (cable) => () =>
    cable.perform({
      channel: "JobsStatusChannel",
      action: "subscribe",
    });

  const handleReceivedMessage = (msg) => {
    const jobToUpdate = jobs.value.find((el) => el.jid === msg.jid);
    if (jobToUpdate) {
      if (msg.status) jobToUpdate.status = msg.status;
    }
  };

  const { resetSearchQuery } = useDebouncedSearchQuery(jobSearchQuery, () => search());

  return {
    // state
    jobs,
    jobSearchQuery,
    jobsCount,
    currentPage,
    currentJob,
    isNew,
    jobAttributes,
    actionDropdownJobs,
    jobTypeFilter,
    mineOnlyFilter,
    activeFilter,
    jobTypeOptions,

    // actions
    getJobsList,
    toggleActiveJob,
    changePage,
    search,
    toggleMineOnlyFilter,
    clearFilters,
    resetAllFilters,
    initRefOnCreate,
    createJob,
    getJob,
    runJob,
    cancelJob,
    updateJob,
    deleteJob,
    jobsChannelHandlers,
  };
});
